import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material';
import { CustomeTextField } from '../generic/CustomeTextField';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Dispatch, SetStateAction, useState } from 'react';
import { ILegalRequirement } from '../Interfaces/ILegalRequirement';
import { ILegalTransactionFullObject } from '../Interfaces/ILegalTransaction';
import { Edit, InsertDriveFile } from '@mui/icons-material';
import { ILegalRequirementState } from '../Interfaces/ILegalRequirementState';
import { DocumentOverview } from '../Document/DocumentOverview';
import { IDocument } from '../Interfaces/IDocument';
import { IFSSelectObject } from '../Interfaces/IFSDirectory';
import React from 'react';

interface IProps {
  currentLegalTransaction: ILegalTransactionFullObject;
  setCurrentLegalTransaction: Dispatch<
    SetStateAction<ILegalTransactionFullObject | null>
  >;
  selectObject: IFSSelectObject;
  setSelectObject: Dispatch<SetStateAction<IFSSelectObject>>;
  legalReqStates: ILegalRequirementState[];
}

export const DocumentPortalScanElementLegalRequirements = ({
  currentLegalTransaction,
  setCurrentLegalTransaction,
  selectObject,
  setSelectObject,
  legalReqStates,
}: IProps) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [isOpenDocumentOverview, setIsOpenDocumentOverview] = useState(false);
  const [selectedReq, setSelectedReq] = useState<ILegalRequirement | null>(
    null
  );
  const [documents, setDocuments] = useState<IDocument[]>([]);

  const handleOpenEmployees = (req: ILegalRequirement) => {
    setDocuments(req.DocumentArrayEmployees || []);
    setIsOpenDocumentOverview(true);
  };

  return (
    <>
      <Dialog
        open={isOpenDocumentOverview}
        onClose={() => setIsOpenDocumentOverview(false)}
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle
          variant='h5'
          sx={{
            borderBottom: '1px solid #ccc', // Optional: Eine Trennlinie für visuelle Klarheit
          }}
        >
          Für den Mandanten bereitgestellte Daten
        </DialogTitle>
        <DialogContent>
          <DocumentOverview
            key={`DocumentOverview-employees`}
            documentArray={documents}
            setDocumentArray={() => {}}
            allowRemove={false}
            allowUpload={false}
            title=''
          />
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: '1px solid #ccc', // Optional: Eine Trennlinie für visuelle Klarheit
          }}
        >
          <Button
            variant='outlined'
            onClick={() => setIsOpenDocumentOverview(false)}
          >
            Abbruch
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Upload für Mitarbeiter aktivieren</DialogTitle>
        <DialogContent>
          Möchten Sie den Upload für Mitarbeiter für diese Maßnahme aktivieren?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDialog(false)}
            color='primary'
            variant='contained'
          >
            Abbrechen
          </Button>
          <Button
            onClick={() => {
              if (selectedReq) {
                const updatedReq = {
                  ...selectedReq,
                  canUploadFilesEmployees: true,
                };

                setCurrentLegalTransaction(prev => {
                  if (!prev) return null;

                  return {
                    ...prev,
                    LegalPhaseArray: prev.LegalPhaseArray.map(phase => ({
                      ...phase,
                      LegalRequirementArray: phase.LegalRequirementArray.map(
                        req =>
                          req.idLegalRequirement ===
                          updatedReq.idLegalRequirement
                            ? updatedReq
                            : req
                      ),
                    })),
                    DataCollection: prev.DataCollection ?? [], // Absicherung für DataCollection
                  };
                });
              }
              setOpenDialog(false);
            }}
            color='success'
            variant='contained'
          >
            Ja
          </Button>
        </DialogActions>
      </Dialog>

      <Grid item xs={12}>
        <Collapse in={selectObject.section === 'legalRequirements'}>
          <Table size='small'>
            <TableBody>
              {currentLegalTransaction.LegalPhaseArray.map(legalPhase => (
                <React.Fragment key={`phase-${legalPhase.idLegalPhase}`}>
                  <TableRow>
                    <TableCell colSpan={12}>
                      <Alert severity='info'>{legalPhase.LegalPhase}</Alert>
                    </TableCell>
                  </TableRow>
                  {legalPhase.LegalRequirementArray.map(req => (
                    <React.Fragment key={`req-${req.idLegalRequirement}`}>
                      <TableRow>
                        <TableCell width={2}></TableCell>
                        <TableCell width={10}>
                          <Tooltip
                            title={
                              req.canUploadFilesEmployees
                                ? ''
                                : 'Upload für Mitarbeiter aktivieren'
                            }
                          >
                            <Alert
                              severity={
                                req.idLegalRequirementState === 40
                                  ? 'success'
                                  : req.enableError &&
                                      req.idLegalRequirementState === 30
                                    ? 'error'
                                    : req.idLegalRequirementState === 15 ||
                                        req.idLegalRequirementState === 20
                                      ? 'warning'
                                      : 'info'
                              }
                              iconMapping={{
                                info: req.canUploadFilesEmployees ? (
                                  <InfoOutlinedIcon />
                                ) : (
                                  <InfoOutlinedIcon sx={{ color: '#616161' }} />
                                ),
                                success: req.canUploadFilesEmployees ? (
                                  <CheckCircleOutlineIcon />
                                ) : (
                                  <CheckCircleOutlineIcon
                                    sx={{ color: '#4caf50' }}
                                  />
                                ),
                                error: req.canUploadFilesEmployees ? (
                                  <ErrorOutlineIcon />
                                ) : (
                                  <ErrorOutlineIcon sx={{ color: '#d32f2f' }} />
                                ),
                                warning: req.canUploadFilesEmployees ? (
                                  <WarningAmberOutlinedIcon />
                                ) : (
                                  <WarningAmberOutlinedIcon
                                    sx={{ color: '#ffa000' }}
                                  />
                                ),
                              }}
                              sx={{
                                ...(req.canUploadFilesEmployees
                                  ? {}
                                  : {
                                      backgroundColor: '#e0e0e0',
                                      color: '#757575',
                                      cursor: 'pointer',
                                    }),
                              }}
                              onClick={() => {
                                if (!req.canUploadFilesEmployees) {
                                  setSelectedReq(req);
                                  setOpenDialog(true);
                                }
                              }}
                              action={
                                <>
                                  {req.canUploadFilesEmployees ? (
                                    <Radio
                                      checked={
                                        selectObject.idLegalRequirement ===
                                        req.idLegalRequirement
                                      }
                                      onChange={() =>
                                        setSelectObject({
                                          ...selectObject,
                                          idLegalRequirement:
                                            req.idLegalRequirement,
                                        })
                                      }
                                      size='small'
                                      disabled={!req.canUploadFilesEmployees}
                                    />
                                  ) : (
                                    <IconButton>
                                      <Edit />
                                    </IconButton>
                                  )}

                                  {req.canUploadFilesEmployees && (
                                    <Tooltip
                                      title={
                                        'Für den Mandanten bereitgestellte Daten'
                                      }
                                    >
                                      <IconButton
                                        onClick={() => handleOpenEmployees(req)}
                                        disabled={!req.canUploadFilesEmployees}
                                      >
                                        <InsertDriveFile />
                                      </IconButton>
                                    </Tooltip>
                                  )}

                                  <Box sx={{ width: 200, ml: 1 }}>
                                    <CustomeTextField
                                      label='Status'
                                      attr='idLegalRequirementState'
                                      object={req}
                                      setObject={(
                                        updatedReq: ILegalRequirement
                                      ) => {
                                        const updatePhase = {
                                          ...legalPhase,
                                          LegalRequirementArray:
                                            legalPhase.LegalRequirementArray.map(
                                              legalReq =>
                                                legalReq.idLegalRequirement ===
                                                updatedReq.idLegalRequirement
                                                  ? updatedReq
                                                  : legalReq
                                            ),
                                        };
                                        setCurrentLegalTransaction({
                                          ...currentLegalTransaction,
                                          LegalPhaseArray:
                                            currentLegalTransaction.LegalPhaseArray.map(
                                              legalPhase =>
                                                legalPhase.idLegalPhase ===
                                                updatePhase.idLegalPhase
                                                  ? updatePhase
                                                  : legalPhase
                                            ),
                                        });
                                      }}
                                      type='select'
                                    >
                                      {legalReqStates.map(legalReqState => (
                                        <MenuItem
                                          key={`idLegalRequirementState-${legalReqState.idLegalRequirementState}`}
                                          value={
                                            legalReqState.idLegalRequirementState
                                          }
                                          disabled={
                                            legalReqState.idLegalRequirementState ===
                                              30 && !req.enableError
                                          }
                                        >
                                          {legalReqState.LegalRequirementState}
                                        </MenuItem>
                                      ))}
                                    </CustomeTextField>
                                  </Box>
                                </>
                              }
                            >
                              {req.LegalRequirement}
                            </Alert>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Collapse>
      </Grid>
    </>
  );
};
